import React from 'react';
import { Form as FinalForm, Field } from 'react-final-form';
import { useHistory } from 'react-router-dom';

import { useIntl } from '../../../../../util/reactIntl';
import { useRouteConfiguration } from '../../../../../context/routeConfigurationContext';
import { createResourceLocatorString } from '../../../../../util/routes';

import { Form } from '../../../../../components';

import IconSearch from './IconSearch';
import css from './HeroSearchForm.module.css';

/**
 * Hero search form. Always navigates to the SearchPage on submit; when
 * the input has a non-empty value it appends a `?keywords=...` query
 * param, otherwise it lands on the empty SearchPage so the user can
 * keep browsing.
 *
 * Two submit triggers in the layout:
 *   - left-side magnifier (visual cue, still a submit button)
 *   - right-side circled arrow CTA (always-on primary affordance)
 *
 * @returns {JSX.Element}
 */
const HeroSearchForm = () => {
  const intl = useIntl();
  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();

  const onSubmit = values => {
    const keywords = values?.keywords?.trim();
    const searchParams = keywords ? { keywords } : {};
    const url = createResourceLocatorString(
      'SearchPage',
      routeConfiguration,
      {},
      searchParams
    );
    history.push(url);
  };

  return (
    <FinalForm
      onSubmit={onSubmit}
      render={({ handleSubmit }) => (
        <Form
          className={css.form}
          onSubmit={handleSubmit}
          enforcePagePreloadFor="SearchPage"
        >
          <button
            type="submit"
            className={css.submit}
            aria-label={intl.formatMessage({ id: 'SectionHero.searchSubmit' })}
          >
            <IconSearch className={css.icon} />
          </button>
          <Field
            name="keywords"
            render={({ input }) => (
              <input
                {...input}
                type="text"
                className={css.input}
                placeholder={intl.formatMessage({ id: 'SectionHero.searchPlaceholder' })}
                autoComplete="off"
              />
            )}
          />
          <button
            type="submit"
            className={css.submitCircle}
            aria-label={intl.formatMessage({ id: 'SectionHero.searchSubmit' })}
          >
            <svg
              className={css.submitCircleIcon}
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M3 8h10M9 4l4 4-4 4"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </Form>
      )}
    />
  );
};

export default HeroSearchForm;
